// What an issue's work shipped as, shown as today's app shows it (Poulpe-205,
// `web/src/vocab.ts` and `lib/releases.ts` there): the release channels its
// landed work touches and awaits, by surface; the versions that released it;
// and its pull requests. An imported issue carries the last two from today's
// board (Poulpe-500). What the server says of them is data: a surface and a
// version are shown as plain text, cut short and without the characters that
// change how text reads, and a pull request links to GitHub only when its
// repository and number are of their form.
import type { Issue, Project, Released } from '../api/types'

/** The most characters of a surface or of a version a page shows. */
const MOST = 40

const plain = (value: string) => value.replace(/[\p{Cc}\p{Cf}]/gu, ' ').trim().slice(0, MOST)

/**
 * Surfaces: what Fabien releases and filters by, in the order the UI shows
 * them. A surface is a release channel under its own name, except `app`.
 */
export const SURFACES: readonly string[] = ['backend', 'app', 'agent', 'website', 'dashboard', 'board']

/** The release channels `app` stands for, in their own order. */
export const APP_CHANNELS: readonly string[] = ['pwa', 'ios', 'android']

/** The surface of a release channel; a channel the web has never heard of is a surface of its own. */
export function surfaceOf(channel: string): string {
  return APP_CHANNELS.includes(channel) ? 'app' : channel
}

/** Surface order: the six, then those the web has never heard of, by name. */
export function compareSurfaces(a: string, b: string): number {
  const rank = (surface: string) => {
    const i = SURFACES.indexOf(surface)
    return i < 0 ? SURFACES.length : i
  }
  return rank(a) - rank(b) || (a < b ? -1 : a > b ? 1 : 0)
}

/** How release channels group into surfaces, and in what order surfaces stand. */
export interface SurfaceNames {
  /** The surface a channel is one of. */
  of: (channel: string) => string
  /** Surface order. */
  compare: (a: string, b: string) => number
}

export const SIX: SurfaceNames = { of: surfaceOf, compare: compareSurfaces }

/** The surfaces of a release map: its names, each its own channel, in the map's order. */
export function mappedSurfaces(names: readonly string[]): SurfaceNames {
  const rank = (surface: string) => {
    const i = names.indexOf(surface)
    return i < 0 ? names.length : i
  }
  return { of: (channel) => channel, compare: (a, b) => rank(a) - rank(b) || (a < b ? -1 : a > b ? 1 : 0) }
}

/** A project's surfaces: its release map's channels in the map's order, else the six. */
export function surfaceNamesOf(project: Pick<Project, 'release_map'> | null | undefined): SurfaceNames {
  const channels = project?.release_map?.channels ?? []
  return channels.length > 0 ? mappedSurfaces(channels.map((channel) => channel.name)) : SIX
}

/** What carries an issue's channel lists. */
export interface ChannelLists {
  touches?: unknown
  awaits?: unknown
}

function channelList(value: unknown): string[] {
  return Array.isArray(value) ? [...new Set(value.filter((c): c is string => typeof c === 'string' && c !== ''))] : []
}

/**
 * The release channels an issue's landed work touches and, among them, those
 * not released yet. A channel it awaits is one it touches, whatever
 * `touches` says.
 */
export function issueChannels(issue: ChannelLists): { touches: string[]; awaits: string[] } {
  const awaits = channelList(issue.awaits)
  return { touches: [...new Set([...channelList(issue.touches), ...awaits])], awaits }
}

/** One surface of an issue's release: its channels the issue touches, and those among them it awaits. */
export interface SurfaceRelease {
  surface: string
  touches: string[]
  awaits: string[]
}

/** An issue's channels by surface, in surface order, `app`'s own in theirs; none for an issue that touches none. */
export function releaseSurfaces(issue: ChannelLists, names: SurfaceNames = SIX): SurfaceRelease[] {
  const { touches, awaits } = issueChannels(issue)
  const bySurface = new Map<string, SurfaceRelease>()
  for (const channel of touches) {
    const surface = names.of(channel)
    const release = bySurface.get(surface) ?? { surface, touches: [], awaits: [] }
    release.touches.push(channel)
    if (awaits.includes(channel)) release.awaits.push(channel)
    bySurface.set(surface, release)
  }
  const inApp = (a: string, b: string) => APP_CHANNELS.indexOf(a) - APP_CHANNELS.indexOf(b)
  const app = bySurface.get('app')
  if (app) {
    app.touches.sort(inApp)
    app.awaits.sort(inApp)
  }
  return [...bySurface.values()].sort((a, b) => names.compare(a.surface, b.surface))
}

/**
 * What shipped an issue, in the order the server gave: the entries that name
 * a surface and a version, as plain text. An issue made here has none.
 */
export function releasedOf(issue: Pick<Issue, 'released'> | null | undefined): Released[] {
  const list: unknown = issue?.released
  if (!Array.isArray(list)) return []
  return list.flatMap((entry: unknown) => {
    const { surface, version } = (typeof entry === 'object' && entry !== null ? entry : {}) as { surface?: unknown; version?: unknown }
    const named = typeof surface === 'string' ? plain(surface) : ''
    return named && typeof version === 'string' && plain(version) ? [{ surface: named, version: stampText(version) ?? plain(version) }] : []
  })
}

/** Stamp chips use UTC, fixed English month names and no locale-dependent timezone conversion. */
function stampText(value: string): string | null {
  const match = /^([0-9]{4})([0-9]{2})([0-9]{2})T([0-9]{2})([0-9]{2})([0-9]{2})Z-[0-9a-f]{7,40}$/.exec(value)
  if (!match || match[0] !== value) return null
  const [, year, month, day, hour, minute, second] = match
  const iso = `${year}-${month}-${day}T${hour}:${minute}:${second}.000Z`
  const date = new Date(iso)
  if (!Number(year) || !Number.isFinite(date.getTime()) || date.toISOString() !== iso) return null
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${Number(day)} ${months[Number(month) - 1]} ${hour}:${minute}`
}

/** One release as it is read: `app 0.1.2`. */
export const releasedName = (entry: Released): string => `${entry.surface} ${entry.version}`

/** The releases of an issue, one after another: `app 0.1.2 · server 0.1.1`. */
export const releasedText = (list: readonly Released[]): string => list.map(releasedName).join(' · ')

/** A repository's address on GitHub, when it is named as GitHub names one. */
export function githubRepository(repository?: string) {
  return /^[A-Za-z0-9][A-Za-z0-9-]{0,38}\/(?!\.{1,2}$)[A-Za-z0-9._-]{1,100}$/.test(repository ?? '') ? `https://github.com/${repository}` : null
}

/** A pull request's address on GitHub, when its repository and number are of their form. */
export function githubPull(repository?: string, number?: number | null) {
  const root = githubRepository(repository)
  return root && number && Number.isSafeInteger(number) && number > 0 ? `${root}/pull/${number}` : null
}

/** An issue's pull requests that have an address, in the server's order: the last is the latest. */
export function pullsOf(issue: Pick<Issue, 'pulls'> | null | undefined): { number: number; url: string }[] {
  const list: unknown = issue?.pulls
  if (!Array.isArray(list)) return []
  return list.flatMap((entry: unknown) => {
    const { repository, number } = (typeof entry === 'object' && entry !== null ? entry : {}) as { repository?: unknown; number?: unknown }
    const url = githubPull(typeof repository === 'string' ? repository : undefined, typeof number === 'number' ? number : null)
    return url ? [{ number: number as number, url }] : []
  })
}
